"use client";

import { defaultModel, type modelID } from "@/ai/providers";
import { Message, useChat } from "@ai-sdk/react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useUser } from "@clerk/nextjs";
import { Textarea } from "./textarea";
import { ProjectOverview } from "./project-overview";
import { Messages } from "./messages";
import { AuthModal } from "./auth-modal";
import { toast } from "sonner";
import { useRouter, useParams } from "next/navigation";
import { getUserId } from "@/lib/user-id";
import { useLocalStorage } from "@/lib/hooks/use-local-storage";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { convertToUIMessages } from "@/lib/chat-store";
import { type Message as DBMessage } from "@/lib/db/schema";
import { nanoid } from "nanoid";
import { useMCP } from "@/lib/context/mcp-context";

// Type for chat data from DB
interface ChatData {
  id: string;
  messages: DBMessage[];
  createdAt: string;
  updatedAt: string;
}

export default function Chat() {
  const router = useRouter();
  const params = useParams();
  const chatId = params?.id as string | undefined;
  const queryClient = useQueryClient();
  const { isSignedIn, user } = useUser();

  const [selectedModel, setSelectedModel] = useLocalStorage<modelID>(
    "selectedModel",
    defaultModel
  );
  const [userId, setUserId] = useState<string>("");
  const [generatedChatId, setGeneratedChatId] = useState<string>("");
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string>("");
  const formRef = useRef<HTMLFormElement>(null);
  const hasRedirected = useRef(false);

  // Get MCP server data from context
  const { mcpServersForApi } = useMCP();

  // Initialize userId - use Clerk ID if signed in
  useEffect(() => {
    const newUserId = getUserId(user?.id);
    setUserId(newUserId);
  }, [user?.id]);

  // Close modal when user signs in
  useEffect(() => {
    if (isSignedIn && showSignInModal) {
      setShowSignInModal(false);
    }
  }, [isSignedIn, showSignInModal]);

  // Auto-submit when user is signed in and we have a pending message
  useEffect(() => {
    if (isSignedIn && !showSignInModal && pendingMessage && formRef.current) {
      // Show toast to indicate message is being sent
      toast.success("Sending your message...", {
        position: "top-center",
        duration: 2000,
      });
      
      // Submit the form
      formRef.current.requestSubmit();
      
      // Clear pending message
      setPendingMessage("");
    }
  }, [isSignedIn, showSignInModal, pendingMessage]);

  // Clear pending message if user closes modal without signing in
  useEffect(() => {
    if (!showSignInModal && !isSignedIn && pendingMessage) {
      setPendingMessage("");
    }
  }, [showSignInModal, isSignedIn, pendingMessage]);

  // Generate a chat ID if needed - regenerate when navigating to home
  useEffect(() => {
    if (!chatId) {
      setGeneratedChatId(nanoid());
    } else {
      // Reset generated ID when viewing an existing chat
      setGeneratedChatId("");
    }
  }, [chatId]);

  // Use React Query to fetch chat history
  const {
    data: chatData,
    isLoading: isLoadingChat,
    error,
    isError,
  } = useQuery({
    queryKey: ["chat", chatId, userId] as const,
    queryFn: async ({ queryKey }) => {
      const [_, chatId, userId] = queryKey;
      
      if (!chatId || !userId) {
        return undefined;
      }

      const response = await fetch(`/api/chats/${chatId}`, {
        headers: {
          "x-user-id": userId,
        },
      });

      if (!response.ok) {
        // For 404 or 403, return a special error object
        if (response.status === 404 || response.status === 403) {
          throw new Error("CHAT_NOT_FOUND");
        }
        throw new Error("Failed to load chat");
      }

      return response.json() as Promise<ChatData>;
    },
    enabled: !!chatId && !!userId, // Only fetch when we have both chatId and userId
    retry: false, // Don't retry on auth failures
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  });

  // Redirect to home if chat doesn't exist or user doesn't have access
  useEffect(() => {
    // Only redirect if:
    // 1. We have a chatId (we're on a chat page)
    // 2. We have a userId (user is initialized)
    // 3. We're not loading
    // 4. There's an error
    // 5. The error is CHAT_NOT_FOUND
    // 6. We haven't already redirected
    // 7. We don't have a pending message (to avoid interfering with auto-submit)
    // 8. The user is loaded (from Clerk)
    if (
      chatId && 
      userId && 
      user !== undefined && // Wait for Clerk to load user state
      !isLoadingChat && 
      isError && 
      error?.message === "CHAT_NOT_FOUND" &&
      !hasRedirected.current &&
      !pendingMessage
    ) {
      hasRedirected.current = true;
      toast.error("Chat not found or you don't have access", {
        position: "top-center",
      });
      // Small delay to ensure smooth transition
      setTimeout(() => {
        router.push("/");
      }, 100);
    }
  }, [chatId, userId, user, isLoadingChat, isError, error, router, pendingMessage]);

  // Reset redirect flag when chatId changes or when we start loading
  useEffect(() => {
    if (chatId) {
      hasRedirected.current = false;
    }
  }, [chatId]);

  // Handle query errors (exclude CHAT_NOT_FOUND as it's handled separately)
  useEffect(() => {
    if (error && error.message !== "CHAT_NOT_FOUND") {
      console.error("Error loading chat history:", error);
      toast.error("Failed to load chat history");
    }
  }, [error]);

  // Prepare initial messages from query data
  const initialMessages = useMemo(() => {
    // Return empty if no chatData or no messages
    if (!chatData || !chatData.messages || chatData.messages.length === 0) {
      return [];
    }

    // Convert DB messages to UI format, then ensure it matches the Message type from @ai-sdk/react
    const uiMessages = convertToUIMessages(chatData.messages);
    return uiMessages.map(
      (msg) =>
        ({
          id: msg.id,
          role: msg.role as Message["role"], // Ensure role is properly typed
          content: msg.content,
          parts: msg.parts,
        } as Message)
    );
  }, [chatData]);

  const { messages, input, handleInputChange, handleSubmit, status, stop } =
    useChat({
      id: chatId || generatedChatId, // Use generated ID if no chatId in URL
      initialMessages,
      maxSteps: 20,
      body: {
        selectedModel,
        mcpServers: mcpServersForApi,
        chatId: chatId || generatedChatId, // Use generated ID if no chatId in URL
        userId,
      },
      experimental_throttle: 50, // Reduced for more frequent UI updates
      onFinish: () => {
        // If this was a new conversation (no chatId in URL), redirect to the chat page immediately
        if (!chatId && generatedChatId) {
          router.push(`/chat/${generatedChatId}`);
        }
        
        // Invalidate the chats query to refresh the sidebar
        if (userId) {
          queryClient.invalidateQueries({ queryKey: ["chats", userId] });
          // Also refresh usage count
          queryClient.invalidateQueries({ queryKey: ["usage", userId] });
        }
      },
      onError: (error) => {
        // Check if it's a premium model access error
        if (error.message.includes("Premium models require") || error.message.includes("PREMIUM_REQUIRED")) {
          toast.error(
            "Premium models require a Plus plan. Please upgrade to access premium models with unlimited usage.",
            { 
              position: "top-center", 
              richColors: true,
              duration: 6000,
              action: {
                label: "Upgrade",
                onClick: () => router.push("/settings"),
              },
            }
          );
          return;
        }
        
        // Check if it's a limit exceeded error
        if (error.message.includes("Monthly message limit reached")) {
          toast.error(
            "You've reached your monthly limit for standard models (100 messages). Upgrade to Plus plan for premium models with unlimited usage. Your quota resets at the start of next month.",
            { 
              position: "top-center", 
              richColors: true,
              duration: 6000,
              action: {
                label: "View Usage",
                onClick: () => router.push("/settings"),
              },
            }
          );
          // Refresh usage to show updated count
          if (userId) {
            queryClient.invalidateQueries({ queryKey: ["usage", userId] });
          }
          return;
        }
        
        toast.error(
          error.message.length > 0
            ? error.message
            : "An error occured, please try again later.",
          { position: "top-center", richColors: true }
        );
      },
    });

  // Custom submit handler with authentication check
  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Validate input
      if (!input.trim()) {
        return;
      }

      // Check if user is authenticated
      if (!isSignedIn) {
        // Save the input for auto-submit after login
        setPendingMessage(input);
        toast.info("Please sign in to send your message", {
          position: "top-center",
          duration: 3000,
        });
        setShowSignInModal(true);
        return;
      }

      // Submit the form - redirect happens in onFinish
      handleSubmit(e);
    },
    [input, handleSubmit, isSignedIn]
  );

  const isLoading =
    status === "streaming" || status === "submitted" || isLoadingChat;

  return (
    <div className="h-full w-full flex flex-col max-w-[430px] sm:max-w-2xl lg:max-w-3xl mx-auto px-3 sm:px-4 py-3">
      {messages.length === 0 && !isLoadingChat ? (
        <div className="flex-1 flex flex-col justify-center w-full">
          <div className="max-w-xl mx-auto w-full">
            <ProjectOverview />
            <form ref={formRef} onSubmit={handleFormSubmit} className="mt-4 w-full mx-auto">
              <Textarea
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                handleInputChange={handleInputChange}
                input={input}
                isLoading={isLoading}
                status={status}
                stop={stop}
                isAuthenticated={isSignedIn}
              />
            </form>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto min-h-0 pb-2 w-full">
            <Messages
              messages={messages}
              isLoading={isLoading}
              status={status}
              isLoadingChat={isLoadingChat}
            />
          </div>
          <form ref={formRef} onSubmit={handleFormSubmit} className="mt-2 w-full shrink-0">
            <Textarea
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              handleInputChange={handleInputChange}
              input={input}
              isLoading={isLoading}
              status={status}
              stop={stop}
              isAuthenticated={isSignedIn}
            />
          </form>
        </>
      )}

      {/* Sign In Modal */}
      <AuthModal open={showSignInModal} onOpenChange={setShowSignInModal} />
    </div>
  );
}
